import { FormControl, FormGroup } from '@angular/forms';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';

export type RoutingMode = 'automatic' | 'custom_url';

// Design fields are nullable: null means "inherit from global"; a value means the merchant
// has explicitly overridden it for this widget. Resolution happens at render time
// (storefront uses `widget.design.X ?? global.buttonDesign.X`).
export type ButtonDesignFormGroup = FormGroup<{
  backgroundColor: FormControl<string | null>;
  textColor: FormControl<string | null>;
  hoverColor: FormControl<string | null>;
  cornerRadius: FormControl<number | null>;
}>;

export interface CheckoutAreaFormShape {
  terms: FormGroup<{
    enabled: FormControl<boolean>;
    text: FormControl<string>;
    textColor: FormControl<string>;
  }>;
  checkoutButton: FormGroup<{
    enabled: FormControl<boolean>;
    label: FormControl<string>;
    design: ButtonDesignFormGroup;
    loaderLabel: FormControl<string>;
    routing: FormControl<RoutingMode>;
    customUrl: FormControl<string>;
  }>;
  viewCartButton: FormGroup<{
    enabled: FormControl<boolean>;
    label: FormControl<string>;
    design: ButtonDesignFormGroup;
    loaderLabel: FormControl<string>;
  }>;
  continueShoppingButton: FormGroup<{
    enabled: FormControl<boolean>;
    label: FormControl<string>;
  }>;
  checkoutMethods: FormGroup<{
    expressCheckout: FormControl<boolean>;
  }>;
  customCode: FormGroup<CustomCodeFormShape>;
}

const DEFAULTS = {
  // The colour the terms row has always rendered in, so adding this control changes nothing.
  terms: { enabled: false, text: '', textColor: '#333333' },
  checkoutButton: {
    enabled: true,
    label: 'Checkout →',
    loaderLabel: 'Checking Out...',
    routing: 'automatic' as RoutingMode,
    customUrl: '',
  },
  viewCartButton: {
    enabled: false,
    label: 'View Cart',
    loaderLabel: 'Redirecting to cart...',
  },
  continueShoppingButton: {
    enabled: false,
    label: 'Continue Shopping',
  },
  checkoutMethods: {
    expressCheckout: false,
  },
};

export const CHECKOUT_AREA_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  {
    reference: '{{terms.enabled}}',
    type: 'Boolean',
    description: 'True when the terms-required toggle is on in the admin',
  },
  {
    reference: '{{terms.text}}',
    type: 'String',
    description: 'Configured terms text — render with triple braces to preserve formatting',
  },
  { reference: '{{terms.textColor}}', type: 'String', description: "Hex of the terms text's colour" },
  {
    reference: '{{checkoutButton.enabled}}',
    type: 'Boolean',
    description: 'True when the checkout button is enabled in the admin',
  },
  { reference: '{{checkoutButton.label}}', type: 'String', description: 'Configured checkout button label' },
  {
    reference: '{{viewCartButton.enabled}}',
    type: 'Boolean',
    description: 'True when the view cart button is enabled in the admin',
  },
  { reference: '{{viewCartButton.label}}', type: 'String', description: 'Configured view cart button label' },
  {
    reference: '{{continueShoppingButton.enabled}}',
    type: 'Boolean',
    description: 'True when the continue shopping button is enabled in the admin',
  },
  {
    reference: '{{continueShoppingButton.label}}',
    type: 'String',
    description: 'Configured continue shopping button label',
  },
  {
    reference: '{{checkoutMethods.expressCheckout}}',
    type: 'Boolean',
    description:
      'True when express checkout (Apple Pay / Google Pay) is enabled in the admin — use to conditionally render <div data-pdq-slot="express-checkout"></div>',
  },
];

export const CHECKOUT_AREA_STARTER_TEMPLATE = `<div
  style="display: flex; flex-direction: column; gap: 12px; padding: 16px 20px; border-top: 1px solid #eee; background-color: #fafafa;"
>
  {{#if terms.enabled}}
    <label style="display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: {{terms.textColor}};">
      <input type="checkbox" data-pdq-input="terms-accepted" style="margin-top: 3px; flex-shrink: 0;" />
      <span>{{{terms.text}}}</span>
    </label>
  {{/if}}

  {{#if checkoutMethods.expressCheckout}}
    <div data-pdq-slot="express-checkout"></div>
  {{/if}}

  {{#if checkoutButton.enabled}}
    <button
      data-pdq-action="checkout"
      style="width: 100%; padding: 14px 24px; font-size: 16px; font-weight: 600; color: white; background-color: #333; border: none; border-radius: 8px; cursor: pointer;"
    >{{checkoutButton.label}}</button>
  {{/if}}

  {{#if viewCartButton.enabled}}
    <button
      data-pdq-action="view-cart"
      style="width: 100%; padding: 12px 24px; font-size: 15px; font-weight: 500; color: #333; background-color: transparent; border: 1px solid #333; border-radius: 8px; cursor: pointer;"
    >{{viewCartButton.label}}</button>
  {{/if}}

  {{#if continueShoppingButton.enabled}}
    <button
      data-pdq-action="continue-shopping"
      style="width: 100%; padding: 6px; font-size: 14px; color: #666; background-color: transparent; border: none; text-decoration: underline; cursor: pointer;"
    >{{continueShoppingButton.label}}</button>
  {{/if}}
</div>`;

function createButtonDesignFormGroup(): ButtonDesignFormGroup {
  return new FormGroup({
    backgroundColor: new FormControl<string | null>(null),
    textColor: new FormControl<string | null>(null),
    hoverColor: new FormControl<string | null>(null),
    cornerRadius: new FormControl<number | null>(null),
  });
}

export function createCheckoutAreaFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<CheckoutAreaFormShape> {
  const formGroup = new FormGroup<CheckoutAreaFormShape>({
    terms: new FormGroup({
      enabled: new FormControl(DEFAULTS.terms.enabled, { nonNullable: true }),
      text: new FormControl(DEFAULTS.terms.text, { nonNullable: true }),
      textColor: new FormControl(DEFAULTS.terms.textColor, { nonNullable: true }),
    }),
    checkoutButton: new FormGroup({
      enabled: new FormControl(DEFAULTS.checkoutButton.enabled, { nonNullable: true }),
      label: new FormControl(DEFAULTS.checkoutButton.label, { nonNullable: true }),
      design: createButtonDesignFormGroup(),
      loaderLabel: new FormControl(DEFAULTS.checkoutButton.loaderLabel, { nonNullable: true }),
      routing: new FormControl(DEFAULTS.checkoutButton.routing, { nonNullable: true }),
      customUrl: new FormControl(DEFAULTS.checkoutButton.customUrl, { nonNullable: true }),
    }),
    viewCartButton: new FormGroup({
      enabled: new FormControl(DEFAULTS.viewCartButton.enabled, { nonNullable: true }),
      label: new FormControl(DEFAULTS.viewCartButton.label, { nonNullable: true }),
      design: createButtonDesignFormGroup(),
      loaderLabel: new FormControl(DEFAULTS.viewCartButton.loaderLabel, { nonNullable: true }),
    }),
    continueShoppingButton: new FormGroup({
      enabled: new FormControl(DEFAULTS.continueShoppingButton.enabled, { nonNullable: true }),
      label: new FormControl(DEFAULTS.continueShoppingButton.label, { nonNullable: true }),
    }),
    checkoutMethods: new FormGroup({
      expressCheckout: new FormControl(DEFAULTS.checkoutMethods.expressCheckout, { nonNullable: true }),
    }),
    customCode: createCustomCodeFormGroup(undefined, CHECKOUT_AREA_STARTER_TEMPLATE),
  });

  if (initialSettings) {
    formGroup.patchValue(initialSettings, { emitEvent: false });
  }

  return formGroup;
}
